import {
  Grid,
  Stack,
  IconButton,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField,
  Typography,
  Divider,
  Tooltip,
  Button,
  Tabs,
  Tab,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import InfoIcon from "@mui/icons-material/Info";
import LockIcon from "@mui/icons-material/Lock";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { FormAutoComplete } from "../../../../components/form/FormAutoComplete";
import { useMedicalCareContext } from "../../../../contexts/MedicalCareContext";
import { useValueSet } from "../../../../hooks/useValueSet";
import { useState, useMemo, useRef } from "react";
import { useWatch } from "react-hook-form";
import { useCustomizationContext } from "../../../../contexts/CustomizationContext";
import { scrollbarStyle } from "../scrollbarStyle";
import { useFormatter } from "@/utils/useFormatter";

interface Finding {
  display: string;
  name: string;
  label: string;
  value: string;
  status?: "normal" | "alterado";
  observation?: string;
  isPredefined?: boolean;
  isFixed?: boolean;
  isCustom?: boolean;
  section?: "Geral" | "Especifico";
}

interface CustomizationItem {
  text: string;
  notEditable: boolean;
}

export function PhysicalFindingsFields({
  selectedTab,
  setSelectedTab,
  isMobile = false,
}: {
  selectedTab: number;
  setSelectedTab: (tab: number) => void;
  isMobile?: boolean;
}) {
  const { control, setValue, unregister } = useMedicalCareContext();
  const {
    itens,
    handleAddItem,
    handleDeleteItem,
    customization,
    saveCustomization,
  } = useCustomizationContext();
  const [searchTerm, setSearchTerm] = useState("");

  const currentSection = selectedTab === 0 ? "Geral" : "Especifico";

  const [tempStatus, setTempStatus] = useState<"normal" | "alterado" | null>(
    null
  );
  const [tempObservation, setTempObservation] = useState("");
  const [inputValue, setInputValue] = useState("");
  const autoCompleteRef = useRef<HTMLInputElement>(null);

  const currentFinding = useWatch({
    control,
    name: "physicalFindings.condition",
  });

  // Observa todos os exames do contexto
  const examsContext = useWatch({
    control,
    name: "exams",
  });

  const registrados: Finding[][] = [
    Object.entries(examsContext?.Geral || {})
      ?.filter(([key]) => key !== "undefined")
      ?.map(([key, value]: [string, any]) => {
        if (value.value) {
          return {
            display: key,
            name: key,
            label: key,
            value: key,
            status: value.value,
            observation: value.alterado,
            section: "Geral",
          };
        }
      }) || [],

    Object.entries(examsContext?.Especifico || {})
      ?.filter(([key]) => key !== "undefined")
      ?.map(([key, value]: [string, any]) => {
        if (value.value) {
          return {
            display: key,
            name: key,
            label: key,
            value: key,
            status: value.value,
            observation: value.alterado,
            section: "Especifico",
          };
        }
      }) || [],
  ] as Finding[][];

  const {
    options: rawOptions,
    loading,
    error,
  } = useValueSet({
    valueSet: "achados-fisicos",
    customUrl: "http://fluxmed.cloud/fhir/ValueSet/achados-fisicos",
    searchTerm,
  });

  // Adapta os itens do contexto para o formato do componente
  const predefinedFindings = useMemo(() => {
    if (!itens) return { Geral: [], Especifico: [] };

    return {
      Geral:
        itens[0]?.itens?.map((item: CustomizationItem) => ({
          display: item.text,
          name: item.text.toLowerCase().replace(/\s+/g, "_"),
          label: item.text,
          value: item.text.toLowerCase().replace(/\s+/g, "_"),
          isPredefined:
            item?.notEditable === true || item?.notEditable === undefined,
          isFixed:
            item?.notEditable === true || item?.notEditable === undefined,
        })) || [],
      Especifico:
        itens[1]?.itens?.map((item: CustomizationItem) => ({
          display: item.text,
          name: item.text.toLowerCase().replace(/\s+/g, "_"),
          label: item.text,
          value: item.text.toLowerCase().replace(/\s+/g, "_"),
          isPredefined:
            item?.notEditable === true || item?.notEditable === undefined,
          isFixed:
            item?.notEditable === true || item?.notEditable === undefined,
        })) || [],
    };
  }, [itens]);

  const options = useMemo(() => {
    const uniqueOptions = rawOptions.reduce((acc: any[], current) => {
      const displayParts = current?.display
        .split("Achados Físicos -")
        .map((part) => part.trim());
      const cleanDisplay = displayParts[displayParts.length - 1];

      const exists = acc.some((item) => item?.display === cleanDisplay);

      if (!exists) {
        acc.push({
          ...current,
          display: cleanDisplay,
          label: cleanDisplay,
        });
      }

      return acc;
    }, []);

    return uniqueOptions;
  }, [rawOptions]);

  const handleAddFinding = () => {
    const displayValue = currentFinding?.display || inputValue;

    if (displayValue && tempStatus) {
      // Atualiza os valores no contexto com o novo padrão de nomeação
      const baseFieldName = `exams.${currentSection}.${displayValue
        ?.toLowerCase()
        ?.replace(/\./g, " ")}`;

      if (tempStatus) {
        setValue(`${baseFieldName}.value`, tempStatus);
        if (tempStatus === "alterado" && tempObservation) {
          setValue(`${baseFieldName}.alterado`, tempObservation);
        }
      }

      setValue("physicalFindings.condition", null);
      setSearchTerm("");
      setTempStatus(null);
      setTempObservation("");
      setInputValue("");

      if (autoCompleteRef.current) {
        const input = autoCompleteRef.current.querySelector(
          "input"
        ) as HTMLInputElement;
        if (input) {
          input.value = "";
        }
      }
    }

    if (
      !itens[selectedTab]?.itens?.find(
        (item: any) =>
          item.text.toLowerCase().replace(/\./g, " ") ===
          displayValue.toLowerCase().replace(/\./g, " ")
      ) &&
      tempStatus
    ) {
      handleAddItem(
        itens[selectedTab]?.specialty,
        displayValue.toLowerCase().replace(/\./g, " ")
      );
    }
  };

  // Verifica se um achado já está registrado
  const isRegistered = (finding: Finding) => {
    return registrados?.[selectedTab]?.some(
      (f) =>
        f?.display?.toLowerCase().replace(/\./g, " ") ===
        finding?.display?.toLowerCase().replace(/\./g, " ")
    );
  };

  const handleRemoveFinding = (findingToRemove: Finding) => {
    if (findingToRemove?.isFixed) {
      return; // Não permite remover itens fixos
    }

    // Remove do contexto
    const baseFieldName = `exams.${
      findingToRemove.section
    }.${findingToRemove?.display.toLowerCase().replace(/\./g, " ")}`;
    setValue(`${baseFieldName}.value`, undefined);
    setValue(`${baseFieldName}.alterado`, undefined);
    unregister(baseFieldName);
  };

  const handleChipClick = (finding: Finding) => {
    const findingValue = {
      display: finding?.display,
      name: finding?.name,
      label: finding?.label,
      value: finding?.value,
    };

    setValue("physicalFindings.condition", findingValue, {
      shouldValidate: true,
    });

    // Atualiza os estados temporários com os valores existentes
    setTempStatus(finding?.status || null);
    setTempObservation(finding?.observation || "");

    setSearchTerm(finding?.display);
    setInputValue(finding?.display);

    if (autoCompleteRef.current) {
      const input = autoCompleteRef.current.querySelector(
        "input"
      ) as HTMLInputElement;
      if (input) {
        input.value = finding?.display;
      }
    }

    handleRemoveFinding(finding);
  };

  const handleRemoveCustomFinding = (findIndex: number) => {
    // Remove da lista de customizados apenas se não estiver registrado

    handleDeleteItem(itens[selectedTab]?.specialty, findIndex);
  };

  return (
    <Stack
      width={isMobile ? "100%" : "50%"}
      sx={{
        // maxHeight: "59vh",
        overflow: "auto",
        ...scrollbarStyle,
      }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Stack>
            <Tabs
              value={selectedTab}
              onChange={(_, newValue) => setSelectedTab(newValue)}
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                mb: 2,
              }}
            >
              <Tab
                value={0}
                label="GERAL"
                sx={{
                  textTransform: "none",
                  fontWeight: "medium",
                }}
              />
              <Tab
                value={1}
                label="ESPECÍFICOS"
                sx={{
                  textTransform: "none",
                  fontWeight: "medium",
                }}
              />
            </Tabs>

            <Stack spacing={1}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{
                  bgcolor: "grey.50",
                  p: 2,
                  borderRadius: 1,
                  border: "1px dashed",
                  borderColor: "grey.300",
                }}
              >
                <InfoIcon color="info" sx={{ fontSize: 20 }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flex: 1 }}
                >
                  {customization ? (
                    <>
                      Você tem "achados customizados" que podem ser salvos para
                      uso futuro. Clique em "Salvar Customizações" para
                      disponibilizar em seus próximos atendimentos.
                    </>
                  ) : (
                    <>
                      Digite um novo achado físico no campo abaixo para criar
                      uma customização. Seus achados customizados poderão ser
                      salvos e reutilizados em futuros atendimentos.
                    </>
                  )}
                </Typography>
                {/* Botão para salvar customizações, visível apenas se customization for true */}
                {customization && (
                  <Tooltip title="Salvar customizações para uso futuro">
                    <span>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={saveCustomization}
                        sx={{ minWidth: 0, px: 2 }}
                      >
                        Salvar Customizações
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Stack>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                {/* Renderiza os achados pré-definidos da seção atual */}
                {predefinedFindings[currentSection].map(
                  (finding: Finding, index: number) => {
                    const alreadyRegistered = isRegistered(finding);
                    return (
                      <Tooltip
                        key={`predefined-${index}`}
                        title={
                          finding?.isFixed
                            ? "Item pré-definido do sistema"
                            : "Item customizado"
                        }
                        arrow
                      >
                        <Chip
                          icon={
                            finding?.isFixed ? (
                              <LockIcon
                                sx={{
                                  fontSize: 16,
                                  ml: 0.5,
                                  opacity: 0.7,
                                }}
                              />
                            ) : undefined
                          }
                          label={useFormatter.formatNameDisplay(
                            finding?.display
                          )}
                          onDelete={
                            finding?.isFixed
                              ? undefined
                              : () => handleRemoveCustomFinding(index)
                          }
                          deleteIcon={
                            !finding?.isFixed ? (
                              <DeleteOutlineIcon
                                sx={{
                                  fontSize: 18,
                                  "&:hover": {
                                    color: "error.main",
                                  },
                                }}
                              />
                            ) : undefined
                          }
                          onClick={() => {
                            if (!alreadyRegistered) {
                              const findingValue = {
                                ...finding,
                                section: currentSection,
                              };
                              setValue(
                                "physicalFindings.condition",
                                findingValue
                              );
                              setSearchTerm(finding?.display);
                              setInputValue(finding?.display);
                              if (autoCompleteRef.current) {
                                const input =
                                  autoCompleteRef.current.querySelector(
                                    "input"
                                  ) as HTMLInputElement;
                                if (input) {
                                  input.value = finding?.display;
                                  input.focus();
                                }
                              }
                            }
                          }}
                          sx={{
                            borderRadius: "8px",
                            opacity: alreadyRegistered ? 0.6 : 1,
                            cursor: alreadyRegistered ? "default" : "pointer",
                            bgcolor: alreadyRegistered ? "grey.100" : "default",
                            "&:hover": {
                              bgcolor: alreadyRegistered
                                ? "grey.100"
                                : "primary.lighter",
                            },
                            "& .MuiChip-label": {
                              color: alreadyRegistered
                                ? "text.disabled"
                                : "default",
                            },
                            "& .MuiChip-icon": {
                              color: "text.secondary",
                            },
                            "& .MuiChip-deleteIcon": {
                              color: "text.secondary",
                              "&:hover": {
                                color: "error.main",
                              },
                            },
                          }}
                        />
                      </Tooltip>
                    );
                  }
                )}
              </div>

              {/* Campo de busca/seleção */}
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "flex-start",
                }}
              >
                <div ref={autoCompleteRef} style={{ flex: 1 }}>
                  <FormAutoComplete
                    freeSolo
                    key={`${registrados[selectedTab]?.length}-${currentSection}`}
                    loading={loading}
                    name="physicalFindings.condition"
                    label={`Selecionar ou adicionar achado ${
                      currentSection === "Geral" ? "Geral" : "específico"
                    }`}
                    control={control}
                    defaultValue={currentFinding}
                    onInputChange={(e: any) => {
                      const value = e?.target?.value;
                      if (value?.length) {
                        const newValue = {
                          display: value,
                          name: value,
                          label: value,
                          value: value,
                          section: currentSection,
                        };
                        setValue("physicalFindings.condition", newValue, {
                          shouldValidate: true,
                        });
                        setSearchTerm(value);
                        setInputValue(value);
                      } else if (!inputValue) {
                        setValue("physicalFindings.condition", null);
                        setSearchTerm("");
                        setInputValue("");
                      }
                    }}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      // Executa handleAddFinding quando Enter for pressionado
                      if (e.key === "Enter") {
                        e.preventDefault(); // Previne comportamento padrão
                        handleAddFinding();
                      }
                    }}
                    selectOptions={options}
                  />
                </div>
                <IconButton
                  onClick={handleAddFinding}
                  disabled={
                    (!inputValue || !currentFinding?.display) && !tempStatus
                  }
                  color="primary"
                  sx={{
                    mt: 1,
                    bgcolor: "primary.main",
                    color: "white",
                    "&:hover": {
                      bgcolor: "primary.dark",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "action.disabledBackground",
                      color: "action.disabled",
                    },
                  }}
                >
                  <AddIcon />
                </IconButton>
              </div>

              {/* Status e Observações */}
              {currentFinding && (
                <div
                  onKeyDown={(e: React.KeyboardEvent) => {
                    // Executa handleAddFinding quando Enter for pressionado
                    if (e.key === "Enter") {
                      e.preventDefault(); // Previne comportamento padrão
                      handleAddFinding();
                    }
                  }}
                >
                  <FormControl component="fieldset" sx={{ width: "100%" }}>
                    <FormLabel component="legend">Status do Achado</FormLabel>
                    <RadioGroup
                      row
                      value={tempStatus}
                      onChange={(e) =>
                        setTempStatus(e.target.value as "normal" | "alterado")
                      }
                    >
                      <FormControlLabel
                        value="normal"
                        control={<Radio color="success" />}
                        label="Normal"
                      />
                      <FormControlLabel
                        value="alterado"
                        control={<Radio color="warning" />}
                        label="Alterado"
                      />
                    </RadioGroup>

                    {tempStatus === "alterado" && (
                      <TextField
                        fullWidth
                        label="Observação"
                        multiline
                        rows={3}
                        value={tempObservation}
                        onChange={(e) => setTempObservation(e.target.value)}
                      />
                    )}
                  </FormControl>
                </div>
              )}
            </Stack>
          </Stack>
          <Stack spacing={3} padding={2}>
            {/* Lista de Achados Registrados */}
            <Stack
              spacing={2}
              sx={{
                borderRadius: 2,
                bgcolor: "#fff",
              }}
            >
              <Typography variant="subtitle1" fontWeight="medium">
                Itens Registrados
              </Typography>

              {registrados?.[selectedTab]?.length > 0 ? (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  {registrados?.[selectedTab]?.map((finding, index) => {
                    return (
                      <Chip
                        key={index}
                        label={`${useFormatter.formatNameDisplay(
                          finding?.display
                        )} ${
                          finding?.status
                            ? finding?.status === "alterado"
                              ? "⚠️"
                              : "✓"
                            : ""
                        }`}
                        onDelete={
                          finding?.isFixed
                            ? undefined
                            : () => handleRemoveFinding(finding)
                        }
                        onClick={() => handleChipClick(finding)}
                        color={
                          finding?.status === "alterado" ? "warning" : "success"
                        }
                        sx={{
                          borderRadius: "8px",
                          "& .MuiChip-label": {
                            px: 1,
                          },
                        }}
                        style={{ cursor: "pointer" }}
                        title={
                          finding?.observation ||
                          (finding?.isFixed
                            ? "Item fixo - não pode ser removido"
                            : "")
                        }
                      />
                    );
                  })}
                </div>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    textAlign: "center",
                    py: 2,
                    px: 3,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                  }}
                >
                  Nenhum achado registrado. Adicione achados físicos usando o
                  campo acima.
                </Typography>
              )}
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}
